import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_LANGS = ["en", "hi", "kn", "ta", "ml", "te", "bn", "mr", "gu", "other"];
const empty = () => ({
  image_url: "", title: {}, subtitle: {}, cta_label: {}, cta_url: "", sort: 0, active: true,
});

export default function AdminBanners() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => setItems((await api.get("/admin/landing_banners")).data);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/landing_banners", form);
      else await api.patch(`/admin/landing_banners/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/admin/landing_banners/${id}`); load();
  };

  const canAdd = items.length < 4;

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Landing page</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Header Banners</h1>
          <p className="font-sans text-sm text-brand-muted mt-2">
            Up to <strong>4 banners</strong> shown on the landing page above the language selector. Auto-rotates every 4.5s. Per-language title & subtitle. Leave empty for the default headline.
          </p>
        </div>
        <Button
          onClick={() => { if (canAdd) { setEditing("new"); setForm(empty()); } }}
          disabled={!canAdd}
          data-testid="banner-new-btn"
          className="rounded-full bg-brand-primary text-brand-bone gap-2 disabled:opacity-40"
        >
          <Plus strokeWidth={1.5} className="w-4 h-4" /> {canAdd ? "Add banner" : "Max 4 banners"}
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line rounded-2xl p-6 space-y-5" data-testid="banner-form">
          <div>
            <Label className="overline">Image URL (16:10 ratio recommended)</Label>
            <Input
              className="mt-2 h-11 rounded-xl border-brand-line"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="https://..."
              required
              data-testid="banner-form-image"
            />
            {form.image_url && (
              <img src={form.image_url} alt="" className="mt-3 w-full max-w-md aspect-[16/10] object-cover rounded-xl bg-brand-elevated" />
            )}
          </div>

          <div>
            <Label className="overline">Title (per language)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input
                    className="h-10 rounded-lg border-brand-line"
                    value={form.title[l] || ""}
                    onChange={(e) => setForm({ ...form, title: { ...form.title, [l]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="overline">Subtitle (per language)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input
                    className="h-10 rounded-lg border-brand-line"
                    value={form.subtitle[l] || ""}
                    onChange={(e) => setForm({ ...form, subtitle: { ...form.subtitle, [l]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="overline">Click action URL (optional — leave blank to make non-clickable)</Label>
            <Input
              className="mt-2 h-11 rounded-xl border-brand-line"
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
              placeholder="https://… or /?to=elvis-6362 or /?category=face"
              data-testid="banner-form-cta-url"
            />
            <p className="text-xs text-brand-muted mt-1.5 leading-snug">
              Examples:<br />
              • <code className="text-brand-dark">https://elvisbeauty.com/about</code> — opens in new tab<br />
              • <code className="text-brand-dark">/?to=elvis-6362</code> — routes funnel to a specific rep<br />
              • <code className="text-brand-dark">/?category=face</code> — pre-selects Face concern card<br />
              • <code className="text-brand-dark">/?to=elvis-6362&amp;category=face</code> — both
            </p>
          </div>

          <div>
            <Label className="overline">CTA label (optional, per language)</Label>
            <p className="text-xs text-brand-muted mt-1 mb-2">Shows as a pill button overlay on the banner. Leave blank for no button.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input
                    className="h-10 rounded-lg border-brand-line"
                    value={form.cta_label[l] || ""}
                    onChange={(e) => setForm({ ...form, cta_label: { ...form.cta_label, [l]: e.target.value } })}
                    placeholder={l === "en" ? "Shop now" : ""}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Sort order</Label>
              <Input type="number" className="mt-2 h-11 rounded-xl border-brand-line" value={form.sort} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })} />
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="font-sans text-sm">Active</Label>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full border-brand-line">Cancel</Button>
            <Button type="submit" className="rounded-full bg-brand-primary text-brand-bone px-6" data-testid="banner-form-submit">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        {items.length === 0 && (
          <p className="font-sans text-sm text-brand-muted col-span-full">No banners yet — landing page shows the default "Your skin, your ritual." headline.</p>
        )}
        {items.map((b) => (
          <div key={b.id} className="bg-white border border-brand-line rounded-2xl overflow-hidden" data-testid={`banner-row-${b.id}`}>
            {b.image_url && <img src={b.image_url} alt="" className="w-full aspect-[16/10] object-cover bg-brand-elevated" />}
            <div className="p-4">
              <p className="font-display text-lg text-brand-dark font-semibold">{b.title?.en || <span className="text-brand-muted">(no title)</span>}</p>
              <p className="font-sans text-xs text-brand-muted mt-1">{b.subtitle?.en || ""}</p>
              <p className="text-[10px] uppercase tracking-widest text-brand-muted mt-2 font-bold">{b.active ? "Active" : "Paused"} · sort {b.sort}</p>
              <div className="flex gap-2 mt-3 justify-end">
                <button
                  onClick={() => {
                    setEditing(b.id);
                    setForm({
                      image_url: b.image_url, title: b.title || {}, subtitle: b.subtitle || {},
                      cta_label: b.cta_label || {}, cta_url: b.cta_url || "",
                      sort: b.sort || 0, active: b.active,
                    });
                  }}
                  className="text-xs text-brand-dark hover:text-brand-primary flex items-center gap-1"
                  data-testid={`banner-edit-${b.id}`}
                >
                  <Pencil strokeWidth={1.5} className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => remove(b.id)} className="text-xs text-brand-dark hover:text-brand-primary flex items-center gap-1" data-testid={`banner-delete-${b.id}`}>
                  <Trash2 strokeWidth={1.5} className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
